import { categories } from "@/mocks/categories";
import { videos, type Video, type VideoType } from "@/mocks/videos";

export type SortKey = "latest" | "hot" | "duration" | "rating";

export const categorySlugToName: Record<string, string> = {
  movie: "激情电影",
  short: "激情短视频",
  theme: "热门主题",
  night: "夜色精选",
};

export function getVideoById(id: string | undefined): Video | undefined {
  if (!id) return undefined;
  return videos.find((item) => item.id === id);
}

export function getCategoryBySlug(slug: string | undefined) {
  return categories.find((item) => item.slug === slug);
}

export function matchesCategory(video: Video, slug: string): boolean {
  if (slug === "movie") return video.type === "movie";
  if (slug === "short") return video.type === "short";
  if (slug === "theme") return video.category === "热门主题" || video.type === "movie";
  if (slug === "night") return video.tags.some((tag) => tag.includes("夜")) || video.tags.includes("深夜") || video.title.includes("夜");
  return true;
}

export function getVideosByCategory(slug: string): Video[] {
  return videos.filter((item) => matchesCategory(item, slug));
}

export function getLatestVideos(limit?: number): Video[] {
  const list = [...videos].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function getHotVideos(limit?: number): Video[] {
  const list = [...videos].sort((a, b) => b.views - a.views);
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  const sameCategory = videos.filter((item) => item.id !== video.id && item.category === video.category);
  const sameType = videos.filter(
    (item) => item.id !== video.id && item.category !== video.category && item.type === video.type,
  );
  const others = videos.filter((item) => item.id !== video.id && item.type !== video.type);
  return [...sameCategory, ...sameType, ...others].slice(0, limit);
}

export interface Filters {
  types: string[];
  regions: string[];
  years: string[];
  durations: string[];
}

export const emptyFilters: Filters = { types: [], regions: [], years: [], durations: [] };

function matchDuration(minutes: number, range: string): boolean {
  if (range === "0-10") return minutes <= 10;
  if (range === "10-60") return minutes > 10 && minutes <= 60;
  if (range === "60-110") return minutes > 60 && minutes <= 110;
  if (range === "110+") return minutes > 110;
  return true;
}

export function applyFilters(list: Video[], filters: Filters): Video[] {
  return list.filter((video) => {
    if (filters.types.length > 0 && !filters.types.includes(video.type)) return false;
    if (filters.regions.length > 0 && !filters.regions.includes(video.region)) return false;
    if (filters.years.length > 0 && !filters.years.includes(String(video.year))) return false;
    if (filters.durations.length > 0 && !filters.durations.some((range) => matchDuration(video.duration, range)))
      return false;
    return true;
  });
}

export function sortVideos(list: Video[], sort: SortKey): Video[] {
  const sorted = [...list];
  if (sort === "latest") return sorted.sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
  if (sort === "hot") return sorted.sort((a, b) => b.views - a.views);
  if (sort === "rating") return sorted.sort((a, b) => b.rating - a.rating);
  if (sort === "duration") return sorted.sort((a, b) => b.duration - a.duration);
  return sorted;
}

export function searchVideos(keyword: string): Video[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return videos.filter((video) => {
    const haystack = [video.title, video.summary, video.description, video.category, video.region, video.director, ...video.tags, ...video.cast]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function typeLabel(type: VideoType): string {
  return type === "movie" ? "电影" : "短视频";
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}分钟`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}小时` : `${hours}小时${rest}分`;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万次播放`;
  return `${views}次播放`;
}

export function formatDate(date: string): string {
  return date.replace(/-/g, ".");
}

export function videoPath(id: string): string {
  return `/detail/${id}`;
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}